/**
 * Transforms a flat list of games into a bracket view model. Two layouts are supported and
 * auto-detected from the data alone (no backend hints needed):
 *
 *   - mode 'rounds'  — gamedays are knockout rounds (e.g. Round of 32 → Round of 16). Each
 *                      gameday holds a distinct set of pairs; we render one column per round.
 *   - mode 'pairs'   — the same team-pair recurs across multiple gamedays, so gamedays are
 *                      legs of a multi-game series (e.g. league playouts). We render one card
 *                      per pair holding all their matches, with no round columns.
 *
 * The distinguishing signal: in single-elimination the same two teams never meet twice, so a
 * pair spanning >1 gameday means the gamedays are legs, not rounds. The DB encodes no bracket
 * topology, so there are no connector lines in either layout.
 *
 * Series are always grouped by team-pair. Within a series, games are ordered chronologically;
 * the first game's home side becomes teamA and every game's scores are aligned to (teamA,
 * teamB) regardless of home/away. The series winner is the side that won more games.
 *
 * Returns { mode, rounds }. In 'pairs' mode `rounds` is a single nameless round holding every
 * series, so both layouts can share the same { id, name, sortingIndex, series } shape.
 */

const sideKey = (side) => side?.teamId || side?.name || ''

const pairKey = (game) => [sideKey(game.homeTeam), sideKey(game.awayTeam)].sort().join('|')

const toTeam = (side) => ({
  teamId: side?.teamId || null,
  name: side?.name || '',
  logoUrl: side?.logoUrl || null,
  color: side?.color || null,
})

const isSameTeam = (side, team) => {
  if (side?.teamId && team.teamId) return side.teamId === team.teamId
  return (side?.name || '') === team.name
}

const byStartDate = (a, b) =>
  new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()

const buildSeries = (seriesGames) => {
  const ordered = [...seriesGames].sort(byStartDate)
  const first = ordered[0]
  const teamA = toTeam(first.homeTeam)
  const teamB = toTeam(first.awayTeam)

  let winsA = 0
  let winsB = 0
  const games = ordered.map((game) => {
    const aIsHome = isSameTeam(game.homeTeam, teamA)
    const aSide = aIsHome ? game.homeTeam : game.awayTeam
    const bSide = aIsHome ? game.awayTeam : game.homeTeam
    const scoreA = aSide?.score ?? null
    const scoreB = bSide?.score ?? null
    if (scoreA != null && scoreB != null) {
      if (scoreA > scoreB) winsA += 1
      else if (scoreB > scoreA) winsB += 1
    }
    return { id: game.id, scoreA, scoreB, status: game.status, startDateTime: game.startDateTime }
  })

  let winner = null
  if (winsA > winsB) winner = 'A'
  else if (winsB > winsA) winner = 'B'

  return { key: pairKey(first), teamA, teamB, games, winsA, winsB, winner }
}

const UNGROUPED_KEY = '__ungrouped__'

const buildBracketRounds = (games = [], gamedays = []) => {
  const gamedayById = new Map(gamedays.map((gd) => [gd.id, gd]))
  const gamedayKeyOf = (game) =>
    gamedayById.has(game.gamedayId) ? game.gamedayId : UNGROUPED_KEY

  // Group every game by team-pair — a pair's full set of matches is one series.
  const byPair = new Map()
  games.forEach((game) => {
    const key = pairKey(game)
    if (!byPair.has(key)) byPair.set(key, [])
    byPair.get(key).push(game)
  })

  const pairs = [...byPair.values()].map((rawGames) => {
    const ordered = [...rawGames].sort(byStartDate)
    return {
      series: buildSeries(ordered),
      gamedayIds: new Set(ordered.map(gamedayKeyOf)),
      roundKey: gamedayKeyOf(ordered[0]),
      firstDate: new Date(ordered[0].startDateTime).getTime(),
    }
  })

  // A pair spanning multiple gamedays ⇒ gamedays are legs of a series, not rounds.
  const mode = pairs.some((p) => p.gamedayIds.size > 1) ? 'pairs' : 'rounds'

  const byFirstDate = (a, b) => a.firstDate - b.firstDate

  if (mode === 'pairs') {
    const series = [...pairs].sort(byFirstDate).map((p) => p.series)
    return { mode, rounds: [{ id: 'pairs', name: '', sortingIndex: 0, series }] }
  }

  // 'rounds': one column per gameday (in order), holding that round's series.
  const pairsByRound = new Map()
  pairs.forEach((p) => {
    if (!pairsByRound.has(p.roundKey)) pairsByRound.set(p.roundKey, [])
    pairsByRound.get(p.roundKey).push(p)
  })

  const toRound = (id, name, sortingIndex, roundPairs) => ({
    id,
    name,
    sortingIndex,
    series: [...roundPairs].sort(byFirstDate).map((p) => p.series),
  })

  const rounds = []
  ;[...gamedays]
    .sort((a, b) => (a.sortingIndex ?? 0) - (b.sortingIndex ?? 0))
    .forEach((gd) => {
      const roundPairs = pairsByRound.get(gd.id)
      if (roundPairs && roundPairs.length) {
        rounds.push(toRound(gd.id, gd.name, gd.sortingIndex ?? 0, roundPairs))
      }
    })

  const ungrouped = pairsByRound.get(UNGROUPED_KEY)
  if (ungrouped && ungrouped.length) {
    rounds.push(toRound(UNGROUPED_KEY, '', Number.MAX_SAFE_INTEGER, ungrouped))
  }

  return { mode, rounds }
}

export default buildBracketRounds
